import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  AssignV3Strategy,
  type AssignV3ActionMode,
} from "@redotech/redo-model/advanced-flow/rules/actions";
import type { AssignV3Action as AssignV3ActionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { GetUser } from "@redotech/redo-model/user";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { catchApiErrors } from "../../../../../../support-v2/db/error-handling";

type ModeTag = AssignV3ActionMode["_tag"];

const modeOptions = [
  {
    id: "specific_users" as const,
    type: "text" as const,
    text: "Specific users",
    value: "specific_users" as ModeTag,
  },
  {
    id: "user_group" as const,
    type: "text" as const,
    text: "User group",
    value: "user_group" as ModeTag,
  },
  {
    id: "unassign" as const,
    type: "text" as const,
    text: "Unassign",
    value: "unassign" as ModeTag,
  },
];

const strategyOptions = [
  {
    id: AssignV3Strategy.ROUND_ROBIN,
    type: "text" as const,
    text: "Round robin",
    value: AssignV3Strategy.ROUND_ROBIN,
  },
  {
    id: AssignV3Strategy.BALANCED,
    type: "text" as const,
    text: "Balanced (fewest open tickets)",
    value: AssignV3Strategy.BALANCED,
  },
];

export const AssignV3Action = memo(function AssignV3Action({
  action,
  onChange,
}: {
  action: AssignV3ActionType;
  onChange: (updated: AssignV3ActionType) => void;
}) {
  const team = useRequiredContext(TeamContext);
  const effectApi = useRequiredContext(EffectApiClientContext);

  const userOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (team?.users || []).map((teamUser) => {
      const user = teamUser.user as GetUser;
      return {
        id: user._id,
        type: "badge" as const,
        value: user._id,
        badge: { text: user.name || user._id },
      };
    });
  }, [team?.users]);

  const userGroupsLoad = useLoad(async () => {
    const client = effectApi.getClient();
    const result = await effectApi.runPromise(
      catchApiErrors(client.userGroup.listUserGroups({})),
    );
    return result.groups;
  }, [effectApi]);

  const groupOptions = useMemo(() => {
    const groups = userGroupsLoad.value ?? [];
    return groups.map((group) => ({
      id: group.id,
      type: "text" as const,
      text: group.name,
      value: group.id,
    }));
  }, [userGroupsLoad.value]);

  // Multi-select dropdown wiring
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedUsers: BadgeRedoListItem<string>[] = useMemo(() => {
    if (action.mode._tag !== "specific_users") return [];
    const selected = new Set(action.mode.mongoUserIds);
    return userOptions.filter((opt) => selected.has(opt.value));
  }, [action.mode, userOptions]);

  const selectedModeOption = modeOptions.find(
    (opt) => opt.value === action.mode._tag,
  );
  const selectedStrategyOption = strategyOptions.find(
    (opt) => opt.value === action.strategy,
  );
  const selectedGroupOption = useMemo(() => {
    const mode = action.mode;
    if (mode._tag !== "user_group") return undefined;
    return groupOptions.find((opt) => opt.value === mode.userGroupId);
  }, [action.mode, groupOptions]);

  const handleModeChange = (newMode: ModeTag) => {
    if (newMode === action.mode._tag) return;
    switch (newMode) {
      case "specific_users":
        onChange({
          ...action,
          mode: { _tag: "specific_users", mongoUserIds: [] },
        });
        break;
      case "user_group":
        onChange({ ...action, mode: { _tag: "user_group", userGroupId: "" } });
        break;
      case "unassign":
        onChange({ ...action, mode: { _tag: "unassign" } });
        break;
      default:
        assertNever(newMode);
    }
  };

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<ModeTag>
        fitToAnchor
        label="Assign to"
        options={modeOptions}
        optionSelected={({ item }) => handleModeChange(item.value)}
        selectedItem={selectedModeOption}
      />

      {action.mode._tag === "specific_users" && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Users ({selectedUsers.length})
          </Text>
          <RedoTagsDropdownInputButton
            disabled={false}
            isDropdownOpen={dropdownOpen}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            placeholder="Select users..."
            ref={setDropdownButtonRef}
            removeItem={(item) => {
              if (action.mode._tag !== "specific_users") return;
              onChange({
                ...action,
                mode: {
                  _tag: "specific_users",
                  mongoUserIds: action.mode.mongoUserIds.filter(
                    (id) => id !== item.value,
                  ),
                },
              });
            }}
            selectedItems={selectedUsers}
            size="xs"
          />
          <RedoMultiselectDropdown<string>
            dropdownAnchor={dropdownButtonRef}
            dropdownOpen={dropdownOpen}
            fitToAnchor
            items={userOptions}
            selectedItems={selectedUsers}
            selectionVariant="checkbox"
            setDropdownOpen={setDropdownOpen}
            setSelectedItems={(items) => {
              onChange({
                ...action,
                mode: {
                  _tag: "specific_users",
                  mongoUserIds: items.map((item) => item.value),
                },
              });
            }}
            size="xs"
          />
        </Flex>
      )}

      {action.mode._tag === "user_group" && (
        <RedoSingleSelectDropdownInput<string>
          fitToAnchor
          label="User group"
          options={groupOptions}
          optionSelected={({ item }) => {
            onChange({
              ...action,
              mode: { _tag: "user_group", userGroupId: item.value },
            });
          }}
          placeholder={
            userGroupsLoad.pending ? "Loading groups..." : "Select a group..."
          }
          selectedItem={selectedGroupOption}
        />
      )}

      {action.mode._tag !== "unassign" && (
        <>
          <RedoSingleSelectDropdownInput<AssignV3Strategy>
            fitToAnchor
            label="Strategy"
            options={strategyOptions}
            optionSelected={({ item }) => {
              onChange({ ...action, strategy: item.value });
            }}
            selectedItem={selectedStrategyOption}
          />

          <Flex dir="column" gap="xs">
            <RedoTextInput
              description="Optional. Users at or above this many open tickets are skipped. Leave blank for no cap."
              inputTypeHint="number"
              label="Max open tickets per user"
              min={1}
              placeholder="No cap"
              setValue={(value) => {
                const trimmed = value.trim();
                if (trimmed === "") {
                  onChange({ ...action, maxTicketsPerUser: null });
                  return;
                }
                const parsed = Number.parseInt(trimmed, 10);
                if (Number.isFinite(parsed) && parsed > 0) {
                  onChange({ ...action, maxTicketsPerUser: parsed });
                }
              }}
              value={
                action.maxTicketsPerUser == null
                  ? ""
                  : String(action.maxTicketsPerUser)
              }
            />
          </Flex>
        </>
      )}
    </Flex>
  );
});
